import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const { FiAlertTriangle, FiX, FiSlash } = FiIcons;

const DisableShareConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  isProcessing = false 
}) => {
  const { t } = useLanguage();

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 dark:border-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10 transform translate-x-1/3 -translate-y-1/3">
              <SafeIcon icon={FiSlash} className="w-32 h-32" />
            </div>
            
            <div className="flex items-center space-x-4 relative z-10">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiAlertTriangle} className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold leading-tight">
                  {t('disableSharingTitle')}
                </h3>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <SafeIcon icon={FiX} className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                {t('disableSharingMessage')}
              </p>
              
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-xl p-4">
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <SafeIcon icon={FiSlash} className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                      {t('shareLinksInvalid')}
                    </span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                      {t('viewersLoseAccess')}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {t('cancel')}
              </button>
              <button
                onClick={onConfirm}
                disabled={isProcessing}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-red-700 transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>{t('processing')}</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSlash} className="w-4 h-4" />
                    <span>{t('confirmDisable')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DisableShareConfirmModal;